import { useEffect } from "react";
import PropTypes from "prop-types";
import { IconButton } from "../buttons";
import { alertTypePropType, toastPositionPropType } from "../../../propTypes";

/**
 * Toast notification component with auto-dismiss
 * Displays temporary notifications that automatically disappear
 *
 * @component
 * @param {Object} props
 * @param {string} props.message - Message to display
 * @param {string} [props.type="info"] - Toast type (success, error, warning, info)
 * @param {Function} props.onClose - Callback to close toast
 * @param {number} [props.duration=5000] - Auto-dismiss duration in ms (0 to disable)
 * @param {string} [props.position="top-right"] - Toast position (top-right, top-left, bottom-right, bottom-left, top-center)
 */
const Toast = ({ message, type = "info", onClose, duration = 5000, position = "top-right" }) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const typeIcons = {
    success: "✓",
    error: "✕",
    warning: "⚠",
    info: "ℹ",
  };

  const icon = typeIcons[type] || typeIcons.info;

  return (
    <div className={`toast-notification toast-${type} toast-${position}`}>
      <div className="toast-content">
        <span className="toast-icon" aria-hidden="true">
          {icon}
        </span>
        <span className="toast-message">{message}</span>
        <IconButton
          icon={<span>×</span>}
          ariaLabel="Close notification"
          onClick={onClose}
          className="toast-close"
          variant="ghost"
          size="sm"
        />
      </div>
    </div>
  );
};

Toast.propTypes = {
  message: PropTypes.string.isRequired,
  type: alertTypePropType,
  onClose: PropTypes.func.isRequired,
  duration: PropTypes.number,
  position: toastPositionPropType,
};

export default Toast;
